import React, { createContext, useContext, useMemo, useState, useEffect } from "react";
import { useAuth } from "..";

const SecurityContext = createContext(null);

/**
 * THE PROVIDER
 * This must be exported as 'SecurityProvider' for App.jsx to find it.
 */
export function SEC_03_03_SecurityProvider({ children }) {
  const { user, isInitialising: authLoading } = useAuth();
  
  // Hydrate matrix from sessionStorage
  const [matrix, setMatrix] = useState(() => {
    const raw = sessionStorage.getItem('SEC_USER_MATRIX');
    try {
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    const raw = sessionStorage.getItem('SEC_USER_MATRIX');
    if (raw) {
      try {
        setMatrix(JSON.parse(raw));
      } catch (e) {
        console.error("Security Matrix Sync Error", e);
      }
    }
  }, [user]);

  const value = useMemo(() => {
    // Extract role codes (e.g., SITEADMIN)
    const derivedRoles = [...new Set(matrix.map(m => 
      String(m.roleCode || m.RoleCode || "").toUpperCase()
    ))].filter(Boolean);

    const isGlobalAdmin = derivedRoles.includes("GLOBALADMIN") || derivedRoles.includes("SITEADMIN");

    return {
      user,
      isInitialising: authLoading,
      roles: derivedRoles,
      matrix,
      isGlobalAdmin,
      can: (action, division = null) => {
        if (isGlobalAdmin) return true;
        const act = String(action).toUpperCase();
        const div = division ? String(division).toUpperCase() : null;
        return matrix.some(m => {
          const mCap = String(m.capabilityCode || "").toUpperCase();
          const mDiv = String(m.divisionCode || "").toUpperCase();
          return div ? (mCap === act && mDiv === div) : (mCap === act);
        });
      }
    };
  }, [user, authLoading, matrix]);

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
}

// 🎯 FIX 1: Explicitly export the Provider name searched by App.jsx
export const SecurityProvider = SEC_03_03_SecurityProvider;

// 🎯 FIX 2: Explicitly export the hook searched by the Guard and Dashboard
export function useSecurity() {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error("useSecurity must be used within a SecurityProvider");
  }
  return context;
}